import { Injectable } from '@angular/core';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { getStandardizeRewards, getTimeSinceLastUpdateFormat, TIMEFORMAT } from '../models/games-parameter-history';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { GameTopPerformer, IHGameParams, IHGameStandardize, IHSelectedMetric } from '../models/games-parameter.model';
import { getConnect3Metrics, getQualifier, getSingleMetric } from '../models/games-player-breakdown';

type GameHistoryParams = {
    [ruleId: number]: {
        [endDate: string]: IHGameStandardize,
    },
};

type TopPerformers = {
    [ruleAndMetric: string]: {
        [endDate: string]: GameTopPerformer[],
    },
};

@Injectable({
    providedIn: 'root'
})
export class GamesParameterHistoryService {
    private localizationMessages = new Map<string, string>();
    private _gameHistoryParams: GameHistoryParams = {};
    private _topPerformers: TopPerformers = {};
    public currentConnect3: IHSelectedMetric = null;
    public lastRuleId: number = null;
    public lastSelectedMetricId: number = null;

    constructor(private cvApiService: CVApiService, private cvLocalizationService: CVLocalizationService) {
        this._gameHistoryParams = {};
        this.cvLocalizationService.GetSection("Games_History").then((result) => {
            this.localizationMessages = result;
        });
    }

    /**
     * 
     * @param number ruleId (game id)
     * @param string endDate (format: YYYY-MM-DD)
     * @returns 
     */
    async loadGameHistoryParams(ruleId: number, endDate: string) : Promise<IHGameStandardize> {
        const url = `/api/history/games/params/${ruleId}/${endDate}`;

        // if we already made this request before, there is no need to do it again
        if (this._gameHistoryParams[ruleId] && this._gameHistoryParams[ruleId][endDate]) return this._gameHistoryParams[ruleId][endDate];

        try {
            const response = await this.cvApiService.get<IHGameParams>(url);
            if (response.status_code === 200) {
                const game = {
                    ...response.data,
                    metricDefinition: getSingleMetric(response.data),
                    qualifierDefinition: getQualifier(response.data),
                    Connect3Definition: getConnect3Metrics(response.data),
                    rewards: getStandardizeRewards(response.data),
                }
                const preParams = this._gameHistoryParams[ruleId] || {};
                this._gameHistoryParams[ruleId] = {
                    ...preParams,
                    [endDate]: game,
                };
                return game;
            }
        } catch (e) {
            showErrorMessage(`Error loading games: ${e.message}`);
        }
    }

    /**
     * 
     * @param number ruleId (game id)
     * @param string endDate (format: YYYY-MM-DD)
     * @returns 
     */
    async getTopPerformers(ruleId: number, startDate: string, endDate: string, metricID: number, qualifier?: number): Promise<GameTopPerformer[]> {
        const url = `/api/history/games/params/${ruleId}/${startDate}/${endDate}/topperformers/${metricID}?qualifier=${qualifier}`;

        // if we already made this request before, there is no need to do it again
        if (this._topPerformers[`${ruleId}-${metricID}`] && this._topPerformers[`${ruleId}-${metricID}`][endDate]) return this._topPerformers[`${ruleId}-${metricID}`][endDate];

        try {
            const response = await this.cvApiService.get<GameTopPerformer[]>(url);
            if (response.status_code === 200) {
                const preParams = this._topPerformers[`${ruleId}-${metricID}`] || {};
                this._topPerformers[`${ruleId}-${metricID}`] = {
                    ...preParams,
                    [endDate]: response.data,
                };
                return response.data;
            }
        } catch (e) {
            showErrorMessage(`Error loading games: ${e.message}`);
        }
    }

    getLocalizationMessage(key: string, defaultValue: string = ''): string {
        if (!this.localizationMessages || !this.localizationMessages.get || !this.localizationMessages.has(key)) return defaultValue;
        return this.localizationMessages.get(key) || defaultValue;
    }

    getGame(ruleId: number, endDate: string) {
        if (!this._gameHistoryParams[ruleId] || !this._gameHistoryParams[ruleId][endDate]) return null;

        return this._gameHistoryParams[ruleId][endDate];
    }

    get gameHistoryParams() {
        return this._gameHistoryParams;
    }

    getStandarizeReward(ruleId: number, endDate: string) {
        const game = this.getGame(ruleId, endDate);
        if (!game) return [];
        return getStandardizeRewards(game);
    }

    getMetrics(ruleId?: number, endDate?: string): IHSelectedMetric[] {
        if (!ruleId || !endDate) return [];
        const game = this.getGame(ruleId, endDate);
        if (!game || !game.HistorySelectedMetrics) return [];
        return game.HistorySelectedMetrics.map(s => {
            if (game.HMetricDetails[s.MetricID]) return {
                ...s,
                MetricDetail: game.HMetricDetails[s.MetricID],
            };
            return s;
        });
    }

    getTimeSinceLastUpdate(ruleId: number, endDate: string): string {
        if (!ruleId || !endDate) return '';
        const game = this.getGame(ruleId, endDate);
        if (!game) return '';
        return getTimeSinceLastUpdateFormat(game.LastUpdateInSeconds, this.getLocalizationMessage.bind(this));
    }

    isRecentlyAdded(ruleId: number, endDate: string, maxDays = 30) {
        const game = this.getGame(ruleId, endDate);
        if (!game) return 0;
        return Math.floor(game.LastUpdateInSeconds / TIMEFORMAT.DAYS) <= maxDays;
    }

    gameUpdated(RuleID?: number) {
        if (RuleID && this._gameHistoryParams[RuleID]) {
            const { [RuleID]: currentRule, ...res} = this._gameHistoryParams;
            const keys = Object.keys(currentRule);
            const currentDate = new Date();
            keys.forEach((key) => {
                if (currentRule[key]) {
                    currentRule[key].LastUpdateInSeconds = 0;
                    currentRule[key].LastUpdate = currentDate;
                }
            });
            this._gameHistoryParams = {
                ...res,
                [RuleID]: currentRule,
            };
        }
    }
}
